import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Container,
  IconButton,
  InputAdornment,
  Typography,
} from '@mui/material';
import { ChangeEvent, ReactNode, useContext, useState } from 'react';
import { ChangeUserPassword, postUpdateUserPassword } from 'api';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { ValidatorForm, TextValidator } from 'react-material-ui-form-validator';
import { NavigateFunction, useNavigate, useParams } from 'react-router';

import LinearProgress from '@mui/material/LinearProgress';
import { Status } from 'types';
import { ROUTES } from 'router';
import { TOAST_TYPE, ToastContext } from 'context/toastContext';
import { useTranslation } from 'react-i18next';
import AuthService from 'utils/AuthService';
import { ThemeContext } from 'context/themeContext';

const ChangePassword = () => {
  const navigate: NavigateFunction = useNavigate();
  const { t } = useTranslation();
  const displayToast = useContext(ToastContext);
  const { logos } = useContext(ThemeContext);
  const { token } = useParams();
  const [{ password, passwordConfirm, id, status }, setFormState] =
    useState<ChangeUserPassword>({
      password: '',
      passwordConfirm: '',
      id: 0,
      status: Status.Active,
    });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [meter, setMeter] = useState(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showPasswordConfirm, setShowPasswordConfirm] =
    useState<boolean>(false);
  const [doPasswordsMatch, setPasswordsMatch] = useState<boolean>(false);
  const [confirmMessage, setShowConfirmMessage] = useState<boolean>(false);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };
  const handleClickShowPasswordConfirm = () => {
    setShowPasswordConfirm(!showPasswordConfirm);
  };

  const getMeterStyle = (): string => {
    return `
          .password-strength-meter {
            height: 18px;
            width: 100%;
            background-color: lightgrey;
            border-radius: 3px;
          }

          .password-strength-meter::before {
            content: "";
                        width: ${(passwordStrength / 5) * 100}%;

            background-color: ${
              ['red', 'orange', '#03a2cc', '#03a2cc', '#0ce052'][
                passwordStrength - 1
              ] || ''
            };
            height: 100%;
            width: 100%;
            display: block;
            border-radius: 3px;
            transition: width 0.2s;`;
  };
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormState({
      ...{
        password,
        passwordConfirm,
        id,
        status,
      },
      [name]: value,
    });
    if (
      name === 'passwordConfirm' &&
      password.match(atLeastOneUppercase) &&
      password.match(atLeastOneLowercase) &&
      password.match(atLeastOneNumeric) &&
      password.match(atLeastOneSpecialChar) &&
      password.match(eightCharsOrMore)
    ) {
      setShowConfirmMessage(true);
      if (password === value) {
        setPasswordsMatch(true);
      } else if (password !== passwordConfirm) {
        setPasswordsMatch(false);
      }
    }
  };
  const handleChangePasswordForm = async () => {
    const status: Status = Status.Active;
    setIsSubmitting(true);
    try {
      const claims = AuthService.DecodeJWT(token || AuthService.GetToken());
      const { id } = claims;
      if (!isMissingInfo()) {
        const res = await postUpdateUserPassword(
          {
            password,
            passwordConfirm,
            id,
            status,
          },
          token || AuthService.GetToken(),
        );
        setIsSubmitting(false);
        if (!res.ok) throw new Error(res.status.toString());
        if (AuthService.IsLoggedIn()) {
          navigate(-1);
        } else {
          navigate(ROUTES.LOGIN);
        }
        displayToast({
          type: TOAST_TYPE.SUCCESS,
          message: t('update_success', { name: t('password'), ns: 'toasts' }),
        });
      }
    } catch (error) {
      displayToast({
        type: TOAST_TYPE.ERROR,
        message: t('update_error', { name: t('password'), ns: 'toasts' }),
      });
    }
  };
  const isMissingInfo = (): boolean => {
    return !password
      ? !password
      : !passwordConfirm
        ? !passwordConfirm
        : passwordStrength !== 5
          ? passwordStrength !== 5
          : !doPasswordsMatch
            ? !doPasswordsMatch
            : isSubmitting;
  };

  const atLeastOneUppercase = /[A-Z]/g; // capital letters from A to Z
  const atLeastOneLowercase = /[a-z]/g; // small letters from a to z
  const atLeastOneNumeric = /\d/g; // numbers from 0 to 9
  const atLeastOneSpecialChar = /[#?!@$%^&*-]/g; // any of the special characters within the square brackets
  const eightCharsOrMore = /.{8,}/g; // eight characters or more
  const passwordTracker = {
    uppercase: password.match(atLeastOneUppercase),
    lowercase: password.match(atLeastOneLowercase),
    number: password.match(atLeastOneNumeric),
    specialChar: password.match(atLeastOneSpecialChar),
    eightCharsOrGreater: password.match(eightCharsOrMore),
  };
  const passwordStrength = Object.values(passwordTracker).filter(
    (value) => value,
  ).length;

  const renderResetPasswordButton = (): ReactNode => {
    return !isSubmitting ? (
      <Button
        centerRipple
        fullWidth
        className='button--sign-in'
        type='submit'
        variant='outlined'
        sx={{ mt: 2, mb: 2 }}
        onClick={handleChangePasswordForm}
        disabled={isMissingInfo()}
      >
        {t('submit')}
      </Button>
    ) : (
      <LinearProgress
        style={{ width: '100%' }}
        className='card'
        color='primary'
      />
    );
  };

  return (
    <Card
      raised
      sx={{
        backgroundColor: 'white',
        flex: 1,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <style>{getMeterStyle()}</style>
      <CardMedia
        component='img'
        image={logos.logoUrl}
        sx={{
          px: `${logos.logoPadding}px`,
          pt: `${logos.logoPadding}px`,
        }}
        alt='logo'
      />
      <CardContent
        sx={{
          padding: {
            xs: 2,
            sm: 4,
          },
          height: {
            xs: '100%',
            sm: 'auto',
          },
          mb: {
            xs: 8,
            sm: 0,
          },
        }}
      >
        <Container component='main' maxWidth='xs'>
          <Typography component='h5' variant='h5' align='center' sx={{ py: 1 }}>
            {t('update')} {t('password')}
          </Typography>
          <ValidatorForm component='form' onSubmit={handleChangePasswordForm}>
            <TextValidator
              fullWidth
              margin={'normal'}
              value={password}
              onFocus={() => setMeter(true)}
              onChange={handleChange}
              name='password'
              label='New Password *'
              type={showPassword ? 'text' : 'password'}
              validators={['required']}
              errorMessages={['Password is required']}
              endAdornment={
                <InputAdornment position='end'>
                  <IconButton
                    aria-label='toggle password visibility'
                    onClick={handleClickShowPassword}
                    edge='end'
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
            />
            {meter && (
              <Box sx={{ mb: 2 }}>
                <div className='password-strength-meter'></div>
                <Typography variant={'caption'} style={{ fontWeight: 'bold' }}>
                  {passwordStrength < 5 && 'Must contain '}
                  {!passwordTracker.uppercase && 'uppercase, '}
                  {!passwordTracker.lowercase && 'lowercase, '}
                  {!passwordTracker.specialChar && 'special character, '}
                  {!passwordTracker.number && 'number, '}
                  {!passwordTracker.eightCharsOrGreater &&
                    'eight characters or more'}
                </Typography>
              </Box>
            )}
            <TextValidator
              type={showPasswordConfirm ? 'text' : 'password'}
              fullWidth
              margin={'normal'}
              variant='outlined'
              value={passwordConfirm}
              onChange={handleChange}
              name='passwordConfirm'
              validators={['required']}
              errorMessages={['Password is required']}
              label='Confirm New Password *'
              endAdornment={
                <InputAdornment position='end'>
                  <IconButton
                    aria-label='toggle password visibility'
                    onClick={handleClickShowPasswordConfirm}
                    edge='end'
                  >
                    {showPasswordConfirm ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
            />
            {confirmMessage && (
              <Typography
                variant={'caption'}
                style={{
                  fontWeight: 'bold',
                  color: doPasswordsMatch ? 'inherit' : 'red',
                }}
              >
                {doPasswordsMatch
                  ? 'Passwords match'
                  : 'Passwords do not match'}
              </Typography>
            )}
            {renderResetPasswordButton()}
          </ValidatorForm>
        </Container>
      </CardContent>
    </Card>
  );
};
export default ChangePassword;
